import { Footer } from "~/components/Footer";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    AggregateQueryMode,
    Filter,
    FilterOperators,
    QueryResponse,
    FilterMetadata,
} from "~/modules/filters/types";
import {
    LeagueTableGroupTypes,
    LeagueTableTab,
} from "~/modules/league_tables/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import { AssetClassIndividualPage } from "~/modules/asset_classes/pages/individual";
import {
    ASSET_CLASS_DESCRIPTIONS,
    ASSET_CLASS_PAGE_TO_SORT,
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS,
    STABLECOIN_ASSETS_QUERY,
    ASSET_CLASS_PAGES_TO_FILTER,
} from "~/modules/asset_classes/consts";
import {
    ASSET_CLASS_IDS,
    MEASURE_IDS,
    MEASURE_SLUGS,
    SOCIAL_OG_IMAGE_URL,
} from "~/consts";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Link } from "~/components/Link";
import { ColumnMetadata } from "~/components/DataGrid";
import { AssetTableRowData } from "~/modules/assets/components/AssetTable/types";
import { NETWORKS_COLUMN } from "~/modules/assets/components/AssetTable/columns/Networks";
import { HOLDING_ADDRESS_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/Holders";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import {
    SIXTY_DAYS_AGO,
    THIRTY_DAYS_AGO,
    THREE_YEARS_AGO,
} from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { getTimeseriesModes } from "~/modules/visualization/components/Timeseries/getTimeseriesModes";
import { ACCOUNT_SEGREGATION_COLUMN } from "~/modules/assets/components/AssetTable/columns/AccountSegregation";
import { TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/ActiveAddresses";
import { BANKRUPCY_REMOTE_COLUMN } from "~/modules/assets/components/AssetTable/columns/BankruptcyRemote";
import { BRIDGED_TOKEN_MARKET_CAP_DOLLAR_COLUMN } from "~/modules/assets/components/AssetTable/columns/BridgedTokenMarketCapDollar";
import { CONTRACT_ADDRESS_COLUMN } from "~/modules/assets/components/AssetTable/columns/ContractAddress";
import { PEGGED_ASSET_COLUMN } from "~/modules/assets/components/AssetTable/columns/PeggedAsset";
import { PLATFORM_COLUMN } from "~/modules/assets/components/AssetTable/columns/Platform";
import { PRICE_COLUMN_STABLECOIN } from "~/modules/assets/components/AssetTable/columns/Price";
import { AUDITOR_COLUMN } from "~/modules/assets/components/AssetTable/columns/ServiceProviders";
import { SP_RATING_COLUMN } from "~/modules/assets/components/AssetTable/columns/SpRating";
import { TRAILING_30_DAY_TRANSFER_COUNT_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferCount";
import { TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN } from "~/modules/assets/components/AssetTable/columns/TransferVolume";
import { INCORPORATION_COUNTRY_COLUMN } from "~/modules/assets/components/AssetTable/columns/IncorporationCountry";
import { LICENSE_OR_REGULATORY_FRAMEWORK } from "~/modules/assets/components/AssetTable/columns/RegulatoryFramework";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE } from "~/modules/assets/components/AssetTable/columns/AssetName";
import { queryTokenAggregateLeagueTable } from "~/modules/league_tables/api/queryTokenAggregateLeagueTable";

// // // //

const SEO_TITLE = "Stablecoins";
const SEO_DESCRIPTION = ASSET_CLASS_DESCRIPTIONS.stablecoins;

// // // //

const STABLECOIN_TABLE_COLUMNS: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
>[] = [
    { ...ASSET_NAME_AND_TICKER_COLUMN_EXPANDABLE, width: 235 },
    BRIDGED_TOKEN_MARKET_CAP_DOLLAR_COLUMN,
    PRICE_COLUMN_STABLECOIN,
    HOLDING_ADDRESS_COUNT_COLUMN,
    PLATFORM_COLUMN,
    NETWORKS_COLUMN,
    PEGGED_ASSET_COLUMN,
    INCORPORATION_COUNTRY_COLUMN,
    LICENSE_OR_REGULATORY_FRAMEWORK,
    SP_RATING_COLUMN,
    ACCOUNT_SEGREGATION_COLUMN,
    BANKRUPCY_REMOTE_COLUMN,
    AUDITOR_COLUMN,
    TRAILING_30_DAY_TRANSFER_VOLUME_COLUMN,
    TRAILING_30_DAY_TRANSFER_COUNT_COLUMN,
    TRAILING_30_DAY_ACTIVE_ADDRESSES_COUNT_COLUMN,
    CONTRACT_ADDRESS_COLUMN,
];

// // // //

// Base filters for all stablecoin queries
const baseFilters: Filter[] =
    ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.stablecoins;

// NOTE - this `fields` array MUST be kept in lockstep with the data that's actually used on this page
// If adding a new column to STABLECOIN_TABLE_COLUMNS, add the corresponding dependent fields to this array
const STABLECOIN_QUERY_FIELDS: string[] = [
    AssetColumns.id,
    AssetColumns.name,
    AssetColumns.slug,
    AssetColumns.ticker,
    AssetColumns.colorHex,
    AssetColumns.iconUrl,
    "price_dollar.val",
    "price_dollar.chg_30d_pct",
    AssetColumns.regulatoryFramework,
    AssetColumns.underlyingAssetClasses,
    AssetColumns.regulatoryFramework,
    AssetColumns.governingBody,
    "sp_rating",
    "issuer",
    "are_accounts_segregated",
    "accounts_segregated_description",
    "custody_description",
    "are_holdings_bankruptcy_remote",
    "holdings_bankruptcy_remote_description",
    "custody_description",
    "transparency_url",
    "transparency_type",
    "transparency_description",
    "pegged_asset",
    // Asset Metrics
    "bridged_token_market_cap_dollar.val",
    "bridged_token_market_cap_dollar.chg_30d_pct",
    "trailing_30_day_active_addresses_count.val",
    "trailing_30_day_active_addresses_count.chg_30d_pct",
    "holding_addresses_count.val",
    "holding_addresses_count.chg_30d_pct",
    "trailing_30_day_transfer_count.val",
    "trailing_30_day_transfer_count.chg_30d_pct",
    "trailing_30_day_transfer_volume.val",
    "trailing_30_day_transfer_volume.chg_30d_pct",
    // Asset Auditor
    "auditor.id",
    "auditor.name",
    "auditor.slug",
    "auditor.icon_url",
    // Token Fields
    "tokens.id",
    "tokens.name",
    "tokens.address",
    // Token.network
    "tokens.network_name",
    "tokens.network.id",
    "tokens.network.name",
    "tokens.network.slug",
    "tokens.network.icon_url",
    "tokens.network.color_hex",
    // Token.platform
    "tokens.platform.id",
    "tokens.platform.name",
    "tokens.platform.slug",
    "tokens.platform.icon_url",
    "tokens.platform.color_hex",
    // Token metrics
    "tokens.bridged_token_market_cap_dollar.val",
    "tokens.bridged_token_market_cap_dollar.chg_30d_pct",
    "tokens.trailing_30_day_active_addresses_count.val",
    "tokens.trailing_30_day_active_addresses_count.chg_30d_pct",
    "tokens.holding_addresses_count.val",
    "tokens.holding_addresses_count.chg_30d_pct",
    "tokens.trailing_30_day_transfer_count.val",
    "tokens.trailing_30_day_transfer_count.chg_30d_pct",
    "tokens.trailing_30_day_transfer_volume.val",
    "tokens.trailing_30_day_transfer_volume.chg_30d_pct",
];

// This function gets called at build time on server-side.
// It may be called again, on a serverless function, if
// revalidation is enabled and a new request comes in
// NOTE - this will have to be changed to hit api.rwa.xyz when the API has been updated
export async function getStaticProps() {
    // Make all requests in parallel + destructure responses
    const [
        listQueryResponse,
        filterMetadata,
        netFlowsData,
        leagueTableTabs,
        marketCapData,
        transferVolumeData,
        activeAddressesData,
        holdersData,
    ] = await Promise.all([
        queryAssets({
            query: {
                ...STABLECOIN_ASSETS_QUERY,
                fields: STABLECOIN_QUERY_FIELDS,
            },
        }),
        queryAssetFilterMeta({
            filter: ASSET_CLASS_PAGES_TO_FILTER.stablecoins,
        }),
        queryNetFlows({
            name: "asset-class--stablecoins",
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            ],
            groupBy: TokenTimeseriesGroupBy.asset,
            limit: 8,
        }),
        queryTokenAggregateLeagueTable({
            name: "asset-class--stablecoins--league-table",
            measureID: MEASURE_IDS.BridgedTokenMarketCapDollar,
            query: {
                filter: {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.Equals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
            },
            groups: [
                LeagueTableGroupTypes.platforms,
                LeagueTableGroupTypes.managers,
                LeagueTableGroupTypes.networks,
                LeagueTableGroupTypes.peggedAssets,
                LeagueTableGroupTypes.jurisdictionCountries,
            ],
            labelOverrides: {
                [LeagueTableGroupTypes.peggedAssets]: {
                    key: "pegged_currency",
                    label: "Pegged Currency",
                    groupColumnLabel: "Currency",
                    enableEntityLinks: false,
                },
            },
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--stablecoins--market-cap",
            measureID: MEASURE_IDS.BridgedTokenMarketCapDollar,
            filters: baseFilters,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--stablecoins--monthly-transfer-volume",
            measureID: MEASURE_IDS.DailyTransferVolumeDollar,
            filters: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrAfter,
                    value: THIRTY_DAYS_AGO.toISOString(),
                },
            ],
            filters30D: [
                ...baseFilters,
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrAfter,
                    value: SIXTY_DAYS_AGO.toISOString(),
                },
                {
                    field: TokenTimeseriesColumns.date,
                    operator: FilterOperators.OnOrBefore,
                    value: THIRTY_DAYS_AGO.toISOString(),
                },
            ],
            mode: AggregateQueryMode.flow,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--stablecoins--monthly-active-addresses",
            measureID: MEASURE_IDS.Trailing30DayActiveAddressesCount,
            filters: baseFilters,
        }),
        queryTokenAggregateValueWith30D({
            name: "asset-class--stablecoins--holders",
            measureID: MEASURE_IDS.DailyNumberOfHolders,
            filters: baseFilters,
        }),
    ]);

    // // // //

    const aggregates: Array<AggregateDisplayProps> = [
        {
            label: "Market Cap",
            tooltip: "Sum of circulating market cap of all tracked stablecoins",
            type: "dollar_compact",
            value: marketCapData.value,
            percentChange: {
                value: marketCapData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Monthly Transfer Volume",
            tooltip:
                "Sum of value transferred on-chain across all tracked stablecoins in the past 30 days.",
            type: "dollar_compact",
            value: transferVolumeData.value,
            percentChange: {
                value: transferVolumeData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Monthly Active Addresses",
            tooltip:
                "Number of on-chain addresses that have transferred a tracked stablecoin in the past 30 days.",
            type: "count_compact",
            value: activeAddressesData.value,
            percentChange: {
                value: activeAddressesData.change30D,
                interval: "30d",
            },
        },
        {
            label: "Holders",
            tooltip:
                "Number of on-chain addresses that have transferred a tracked stablecoin in the past 30 days.",
            type: "count_compact",
            value: holdersData.value,
            percentChange: {
                value: holdersData.change30D,
                interval: "30d",
            },
        },
    ];

    // Trim down FilterMetadata options to only include the ones we need
    const trimmedFilterMetadata = filterMetadata
        .filter((each) => {
            return [
                AssetColumns.networkIDs,
                AssetColumns.peggedAssetID,
            ].includes(each.field as AssetColumns);
        })
        .sort((a, b) => {
            return a.field.localeCompare(b.field);
        })
        // Rename `Pegged Asset` to `Pegged Currency`
        .map((each) => {
            if (each.field === AssetColumns.peggedAssetID) {
                return {
                    ...each,
                    content: {
                        ...each.content,
                        label: "Pegged Currency",
                    },
                };
            }
            return each;
        });

    return {
        props: {
            listQueryResponse,
            filterMetadata: trimmedFilterMetadata,
            leagueTableTabs,
            aggregates,
            netFlows: netFlowsData,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

export default function StablecoinsPage(props: {
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
    aggregates: Array<AggregateDisplayProps>;
    leagueTableTabs: LeagueTableTab[];
    netFlows: NetFlowsData;
}) {
    const {
        listQueryResponse,
        filterMetadata,
        leagueTableTabs,
        netFlows,
        aggregates,
    } = props;

    const modes = getTimeseriesModes({
        analyticsKey: "stablecoin",
        defaultGroupBy: TokenTimeseriesGroupBy.asset,
        measureSlug: MEASURE_SLUGS.bridged_token_market_cap_dollar,
        groupByOptions: [
            TokenTimeseriesGroupBy.asset,
            TokenTimeseriesGroupBy.token,
            TokenTimeseriesGroupBy.network,
            TokenTimeseriesGroupBy.protocol,
            TokenTimeseriesGroupBy.manager,
            TokenTimeseriesGroupBy.jurisdictionCountry,
        ],
        navigator: {
            enabled: true,
            active: true,
            forceUpdate: true,
            xAxis: {
                min: THREE_YEARS_AGO.getTime(),
                max: null,
            },
        },
        filters: ASSET_CLASS_PAGE_TO_TOKEN_TIMESERIES_FILTERS.stablecoins,
    });

    return (
        <PageTrack>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.stablecoins)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.stablecoins),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "stablecoins, USDT, USDC, DAI, stablecoin market cap, stablecoin supply, fiat-backed stablecoins, crypto stablecoins, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.stablecoins}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.stablecoins,
                    },
                ]}
            />
            <UserFetcher>{({ user }) => <Navbar user={user} />}</UserFetcher>
            <SidebarLayout pathname={Routes.stablecoins}>
                {/* <Breadcrumbs
                    pages={[
                        {
                            name: "Asset Classes",
                            current: true,
                        },
                        {
                            name: "Stablecoins",
                            current: true,
                        },
                    ]}
                /> */}
                <PageWrapper>
                    <AssetClassIndividualPage
                        listQueryResponse={listQueryResponse}
                        netFlows={{
                            title: "Stablecoin Net Flows",
                            data: netFlows,
                        }}
                        title={SEO_TITLE}
                        subtitle={SEO_DESCRIPTION}
                        assetsQuery={{
                            ...STABLECOIN_ASSETS_QUERY,
                            fields: STABLECOIN_QUERY_FIELDS,
                        }}
                        header={{
                            label: "Stablecoins",
                            loading: false,
                            value: `${listQueryResponse.results.length.toLocaleString()} Total`,
                            children: (
                                <div className="ml-auto mr-4 w-auto text-blue-500 text-xs font-light bg-blue-50 border border-blue-800/30 shadow-xs p-2 rounded-sm hidden lg:block">
                                    📣 We now support{" "}
                                    <span className="font-bold">
                                        asset reserves
                                    </span>{" "}
                                    data for stablecoins.{" "}
                                    <Link
                                        href={Routes.salesPage}
                                        className="underline"
                                        analyticsLabel="Stablecoins Table - Asset Reserves CTA"
                                    >
                                        Learn more here.
                                    </Link>
                                </div>
                            ),
                        }}
                        aggregates={aggregates}
                        timeseriesChart={{
                            title: "Stablecoin Metrics",
                            modes,
                            distributionSelect: {
                                distributed: {
                                    enabled: false,
                                },
                                represented: {
                                    enabled: false,
                                },
                                stablecoins: {
                                    enabled: false,
                                    active: true,
                                },
                            },
                            columnSpanConfig: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 12,
                                xxl: 12,
                            },
                        }}
                        pieChart={{
                            leagueTableData: {
                                all: leagueTableTabs,
                                distributed: [],
                                represented: [],
                            },
                            title: "Stablecoin Market Caps",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                            defaultGroupBy: LeagueTableGroupTypes.networks,
                            allowedGroupByValues: [
                                LeagueTableGroupTypes.assets,
                                LeagueTableGroupTypes.tokens,
                                LeagueTableGroupTypes.networks,
                                LeagueTableGroupTypes.peggedAssets,
                                LeagueTableGroupTypes.jurisdictionCountries,
                            ],
                            groupByLabelOverrides: {
                                [LeagueTableGroupTypes.peggedAssets]:
                                    "Pegged Currency",
                            },
                        }}
                        scatterChart={{
                            title: "Stablecoin Market Caps vs. Volume",
                            totalValueMeasureSlug:
                                MEASURE_SLUGS.bridged_token_market_cap_dollar,
                            totalValueMeasureName: "Market Cap",
                            defaultGroupBy: TokenTimeseriesGroupBy.asset,
                            allowedGroupByValues: [
                                TokenTimeseriesGroupBy.asset,
                                TokenTimeseriesGroupBy.token,
                                TokenTimeseriesGroupBy.protocol,
                                TokenTimeseriesGroupBy.parentNetwork,
                                TokenTimeseriesGroupBy.peggedAsset,
                            ],
                            groupByLabelOverrides: {
                                [TokenTimeseriesGroupBy.peggedAsset]:
                                    "Currency",
                            },
                            filters: [
                                {
                                    field: TokenTimeseriesColumns.assetClassID,
                                    operator: FilterOperators.Equals,
                                    value: ASSET_CLASS_IDS.Stablecoin,
                                },
                            ],
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        leagueTable={{
                            leagueTableData: {
                                all: [],
                                distributed: leagueTableTabs,
                                represented: [],
                            },
                            analyticsTableName: "stablecoin-league-table",
                            title: "Stablecoin League Table",
                            colSpans: {
                                sm: 12,
                                md: 12,
                                lg: 12,
                                xl: 6,
                                xxl: 6,
                            },
                        }}
                        table={{
                            filterMetadata,
                            columns: STABLECOIN_TABLE_COLUMNS,
                            sortQuery: ASSET_CLASS_PAGE_TO_SORT.stablecoins,
                            analyticsTableName: "stablecoins",
                            exportFilename: "stablecoins-assets",
                        }}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
